import { useState } from "react";
import { api } from "@/lib/api";

const CATEGORIES = [
  { value: "general", label: "General question" },
  { value: "speaking", label: "Speaking engagement" },
  { value: "media", label: "Media / press" },
  { value: "bulk", label: "Bulk copies" },
];

const inputCls =
  "w-full bg-transparent border border-paper/20 focus:border-crimson rounded-none px-4 py-3.5 font-ui text-sm text-paper placeholder:text-paper/30 transition-colors duration-300 outline-none";

export function SignupForm({ heading, body, dark = true }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setMessage("");
    try {
      const r = await api.post("/public/subscribe", { email, consent });
      setState("success");
      setMessage(r.data.message);
    } catch (err) {
      setState("error");
      setMessage(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (state === "success") {
    return (
      <div className="border border-gold/30 p-8 text-center" data-testid="signup-success">
        <p className="font-display text-xl text-gold tracking-wide">You are on the list.</p>
        <p className="font-ui text-sm text-paper/60 mt-3">{message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="reader-signup-form" className="space-y-4">
      {heading && <h3 className="font-display text-2xl sm:text-3xl text-paper">{heading}</h3>}
      {body && <p className="font-ui text-sm text-paper/60 leading-relaxed">{body}</p>}
      <label htmlFor="signup-email" className="sr-only">Email address</label>
      <input
        id="signup-email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email address"
        className={inputCls}
        data-testid="signup-email-input"
      />
      <label className="flex items-start gap-3 cursor-pointer" data-testid="signup-consent-label">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
          className="mt-1 accent-[#B71F32]"
          data-testid="signup-consent-checkbox"
        />
        <span className="font-ui text-xs text-paper/50 leading-relaxed">
          I agree to receive release news, approved excerpts, and appearance updates from Dirty Church Chronicles.
        </span>
      </label>
      {state === "error" && (
        <p className="font-ui text-sm text-crimsonhi" role="alert" data-testid="signup-error">{message}</p>
      )}
      <button
        type="submit"
        disabled={state === "sending"}
        data-testid="signup-submit-btn"
        className="font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-crimson text-paper px-8 py-4 rounded-full transition-[background-color,transform,opacity] duration-300 hover:bg-crimsonhi hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
      >
        {state === "sending" ? "Joining…" : "Join the Reader List"}
      </button>
    </form>
  );
}

export function InquiryForm({ defaultCategory = "general" }) {
  const [form, setForm] = useState({ name: "", email: "", category: defaultCategory, message: "" });
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setMessage("");
    try {
      const r = await api.post("/public/inquiries", form);
      setState("success");
      setMessage(r.data.message);
    } catch (err) {
      setState("error");
      setMessage(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (state === "success") {
    return (
      <div className="border border-gold/30 p-8" data-testid="inquiry-success">
        <p className="font-display text-xl text-gold tracking-wide">Message received.</p>
        <p className="font-ui text-sm text-paper/60 mt-3">{message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="inquiry-form" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="inq-name" className="sr-only">Your name</label>
          <input id="inq-name" required value={form.name} onChange={set("name")} placeholder="Your name"
            className={inputCls} data-testid="inquiry-name-input" />
        </div>
        <div>
          <label htmlFor="inq-email" className="sr-only">Your email</label>
          <input id="inq-email" type="email" required value={form.email} onChange={set("email")}
            placeholder="Your email" className={inputCls} data-testid="inquiry-email-input" />
        </div>
      </div>
      <div>
        <label htmlFor="inq-category" className="sr-only">Inquiry type</label>
        <select id="inq-category" value={form.category} onChange={set("category")}
          className={`${inputCls} bg-ink`} data-testid="inquiry-category-select">
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="inq-message" className="sr-only">Your message</label>
        <textarea id="inq-message" required rows={5} value={form.message} onChange={set("message")}
          placeholder="How can we help?" className={`${inputCls} resize-y`} data-testid="inquiry-message-input" />
      </div>
      {state === "error" && (
        <p className="font-ui text-sm text-crimsonhi" role="alert" data-testid="inquiry-error">{message}</p>
      )}
      <button
        type="submit"
        disabled={state === "sending"}
        data-testid="inquiry-submit-btn"
        className="font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-paper text-ink px-8 py-4 rounded-full transition-[background-color,transform,opacity] duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
      >
        {state === "sending" ? "Sending…" : "Send Inquiry"}
      </button>
    </form>
  );
}

export function ShareButtons({ title, text, path }) {
  const [copied, setCopied] = useState(false);
  const url = `https://dirtychurchchronicles.com${path}`;

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (_) { /* user cancelled */ }
    }
    copy();
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  };

  return (
    <div className="flex gap-3">
      <button onClick={share} data-testid="share-btn"
        className="font-ui text-[11px] uppercase tracking-[0.2em] border border-current px-5 py-2.5 rounded-full transition-opacity duration-300 hover:opacity-70">
        Share
      </button>
      <button onClick={copy} data-testid="copy-link-btn"
        className="font-ui text-[11px] uppercase tracking-[0.2em] border border-current px-5 py-2.5 rounded-full transition-opacity duration-300 hover:opacity-70">
        {copied ? "Link copied" : "Copy link"}
      </button>
    </div>
  );
}
